// 立即从localStorage恢复token，确保刷新页面时token立即可用
// 使用安全读取，防止浏览器「跟踪防护」阻断 storage 时抛错
import { safeLocalStorageGet, safeLocalStorageSet, safeLocalStorageRemove } from '@/utils'
let initialToken = (safeLocalStorageGet('token') || '').trim()

const state = {
  token: initialToken,
  user: null, // 不缓存用户信息，每次都从API获取确保准确性
  enterpriseInfo: null, // 不缓存企业信息，每次都从API获取确保准确性
  isAuthenticated: !!initialToken
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
    state.isAuthenticated = !!token
    if (token) {
      safeLocalStorageSet('token', token)
    } else {
      safeLocalStorageRemove('token')
    }
  },
  SET_USER(state, user) {
    state.user = user
    // 不再缓存到localStorage，确保每次都从API获取最新数据
  },
  SET_ENTERPRISE_INFO(state, enterpriseInfo) {
    state.enterpriseInfo = enterpriseInfo
    // 不再缓存到localStorage，确保每次都从API获取最新数据
  },
  LOGOUT(state) {
    state.token = ''
    state.user = null
    state.enterpriseInfo = null
    state.isAuthenticated = false
    safeLocalStorageRemove('token')
  }
}

const actions = {
  async login({ commit, rootState }, { token, user }) {
    // 登录时清除旧的localStorage缓存，避免账号混乱
    safeLocalStorageRemove('user')
    safeLocalStorageRemove('enterpriseInfo')
    
    commit('SET_TOKEN', token)
    commit('SET_USER', user)
    
    // 发送登录同步消息
    if (rootState.authSync) {
      rootState.authSync.sendLoginSync(token, user)
    }
    
    // 如果是企业或服务用户，同步获取企业信息
    if (user && (user.user_type === 'enterprise' || user.user_type === 'service')) {
      try {
        console.log('🔍 开始获取企业/服务信息...')
        let enterpriseInfo = null
        
        if (user.user_type === 'enterprise') {
          // 企业用户调用企业API
          const { enterpriseAPI } = await import('@/utils/api')
          enterpriseInfo = await enterpriseAPI.getMyEnterprise()
        } else if (user.user_type === 'service') {
          // 服务用户调用服务API
          const { serviceAPI } = await import('@/utils/api')
          enterpriseInfo = await serviceAPI.getMyServices()
        }
        
        console.log('🔍 企业/服务信息获取成功:', enterpriseInfo)
        
        commit('SET_ENTERPRISE_INFO', enterpriseInfo)
        console.log('✅ 企业/服务信息已保存到store')
      } catch (error) {
        console.error('❌ 获取企业/服务信息失败:', error)
        // 如果是404错误，说明还没有企业/服务信息，这是正常的（新注册用户）
        if (error.response && error.response.status === 404) {
          console.log('企业/服务信息不存在，这是正常的（新注册用户）')
        }
        // 不阻止登录流程，只是记录错误
        commit('SET_ENTERPRISE_INFO', null)
      }
    } else {
      // 个人用户，清除企业信息
      commit('SET_ENTERPRISE_INFO', null)
    }
  },
  logout({ commit, rootState }) {
    commit('LOGOUT')
    
    // 发送退出同步消息
    if (rootState.authSync) {
      rootState.authSync.sendLogoutSync()
    }
  },
  // 新增：刷新时自动获取用户信息
  async syncUser({ commit, state }) {
    let token = (safeLocalStorageGet('token') || '').trim() || state.token
    console.log('🔍 syncUser - 检查token:', { stateToken: state.token, localStorageToken: token })
    
    if (token) {
      // 如果store中没有token，从localStorage恢复
      if (!state.token) {
        commit('SET_TOKEN', token)
      }
      
      try {
        const { authAPI } = await import('@/utils/api')
        const res = await authAPI.getUserInfo()
        console.log('🔍 syncUser API响应:', res)
        
        // 处理不同的响应格式
        let userData = null
        if (res && res.data) {
          // 包装在data字段中的情况
          userData = res.data
        } else if (res && res.username) {
          // 直接返回用户信息的情况
          userData = res
        }
        
        if (userData) {
          commit('SET_USER', userData)
          console.log('✅ 用户信息已保存到store:', userData)
          
          // 如果是企业或服务用户，重新获取企业信息
          if (userData && (userData.user_type === 'enterprise' || userData.user_type === 'service')) {
            try {
              let enterpriseInfo = null
              
              if (userData.user_type === 'enterprise') {
                // 企业用户调用企业API
                const { enterpriseAPI } = await import('@/utils/api')
                enterpriseInfo = await enterpriseAPI.getMyEnterprise()
              } else if (userData.user_type === 'service') {
                // 服务用户调用服务API
                const { serviceAPI } = await import('@/utils/api')
                enterpriseInfo = await serviceAPI.getMyServices()
              }
              
              commit('SET_ENTERPRISE_INFO', enterpriseInfo)
              console.log('✅ 已重新获取企业/服务信息到store')
            } catch (e) {
              // 如果是404错误，说明还没有企业/服务信息，这是正常的
              if (e.response && e.response.status === 404) {
                console.log('企业/服务信息不存在，这是正常的（新注册用户）')
              } else {
                console.error('获取企业/服务信息失败:', e)
              }
            }
          }
        } else {
          // 如果没有用户数据，清除登录状态
          console.log('❌ 没有用户数据，清除登录状态')
          commit('LOGOUT')
        }
      } catch (e) {
        // 静默处理401错误（未登录是正常情况，不需要记录错误）
        if (e.response?.status !== 401) {
          console.error('同步用户信息失败:', e)
        }
        // 对于401错误，需要更谨慎处理
        // 在生产环境中，可能是token过期或无效，但也可能是网络问题
        // 只有在明确是认证失败时才清除token
        if (e.response?.status === 401) {
          // /auth/user 返回 401 表示未登录、token 无效或用户已被删除，一律清除 token
          console.log('⚠️ 同步用户信息时遇到401，清除token，需要重新登录')
          commit('LOGOUT')
        } else {
          // 非401错误（如网络错误），保留登录状态
          // 因为可能是网络问题，不是token问题
          console.log('⚠️ 同步用户信息失败，但保留登录状态，可能是网络问题')
        }
      }
    } else {
      // 如果没有token，确保用户状态为未登录
      console.log('❌ 没有token，清除登录状态')
      commit('LOGOUT')
    }
  }
}

const getters = {
  isAuthenticated: state => state.isAuthenticated,
  user: state => state.user,
  token: state => state.token,
  enterpriseInfo: state => state.enterpriseInfo
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 